<template>
    <div
        :id="id"
        class="tl-inputdate"
    >
        <div ref="container">
            <InputText
                v-model="modelState"
                type="text"
                :label="label"
                :disabled="disabled"
                :focus="focus"
                :parent-focused="focused"
                :error="!!computedError"
                :placeholder="placeholder"
                @focus="focused = true"
                @blur="handleBlur"
                @submit="emit('submit', $event)"
                @mounted="addMaska"
            />
            <Datepicker
                ref="datePicker"
                :model-value="modelState"
                calendar-class-name="tl-calendar"
                menu-class-name="tl-calendar-menu"
                :model-type="datePickerFormat"
                :format="datePickerFormat"
                :enable-time-picker="isDateTimePicker"
                :disabled-week-days="disabledWeekDays"
                :disabled-dates="disabledDates"
                :min-date="min && new Date(min)"
                :max-date="max && new Date(max)"
                :min-time="minTime"
                :max-time="maxTime"
                text-input
                :teleport="teleportRoot"
                :teleport-center="teleportDirection === 'center'"
                :alt-position="teleportPosition"
                :minutes-increment="minutesIncrement"
                :minutes-grid-increment="minutesGridIncrement"
                :start-time="startTime"
                :locale="locale"
                :disabled="disabled"
                six-weeks="append"
                arrow-navigation
                @update:model-value="handlePickerValue"
                @open="revealDatePicker"
                @closed="hideDatePicker"
            >
                <template #arrow-left>
                    <i class="mi-chevron-left"></i>
                </template>

                <template #arrow-right>
                    <i class="mi-chevron-right"></i>
                </template>

                <template #trigger>
                    <i
                        tabindex="0"
                        class="calendar-trigger mi-calendar"
                        :data-testid="getDataTestId('trigger')"
                        @click.stop="toggleDatePicker(false)"
                        @keydown="handleCalendarTriggerKeyDown"
                    />
                </template>

                <template #action-row="{ internalModelValue, selectDate }">
                    <div class="action-row">
                        <ActionButton
                            ref="actionButton"
                            :label="applyLabel || t('apply')"
                            secondary
                            :disabled="!isBetweenMinMax(internalModelValue)"
                            :test-id="getDataTestId('select-date')"
                            @click="selectDate"
                        />
                    </div>
                </template>
            </Datepicker>
        </div>
        <ErrorText
            v-if="!cpIsLoading && computedError"
            :error="computedError"
        />
    </div>
</template>

<script setup lang="ts">
import {
    ref, watch, computed, onMounted, inject, toRaw, reactive, nextTick,
    onBeforeUnmount,
    useTemplateRef,
} from 'vue'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import ActionButton from '../ActionButton/ActionButton.vue'
import Datepicker from '@vuepic/vue-datepicker'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import marked from '../../utilities/marked'
import { useI18n } from '../../utilities/i18n'
import InputText from 'widget-components/components/InputText/InputText.vue'
import { MaskInput } from 'maska'
import useCustomCss from 'widget-components/composables/custom-css'
import { clamp, isNotEmpty } from 'ramda'
import { useSpaceAvailability } from 'widget-components/composables/space-availability'
import { isClient } from '@vueuse/core'
import type { InputDateTimeProps } from './types'

import '@vuepic/vue-datepicker/dist/main.css'

const { t, locale } = useI18n()

dayjs.extend(customParseFormat)
dayjs.extend(utc)

defineOptions({ inheritAttrs: false })

const container = useTemplateRef<HTMLDivElement>('container')
const teleportRoot = isClient
    ? document.getElementsByClassName('widget-wrapper')[0] as HTMLElement | undefined || document.body
    : undefined

const props = withDefaults(defineProps<InputDateTimeProps & { focus?: boolean }>(), {
    format: 'YYYY-MM-DD HH:mm',
    applyLabel: '',
})

const emit = defineEmits(['update:modelValue', 'submit'])

const dayjsFormat = computed(() => props.format
    .replace(/y/g, 'Y')
    .replace(/d/g, 'D')
    .replace(/h/g, 'H'),
)

const datePickerFormat = computed(
    () => dayjsFormat.value.toLowerCase()
        .replace(/([mh])/g, (_, g) => g.toUpperCase())
        .replace(/MM$/, 'mm'),
)

const isDateTimePicker = computed(() => /[Hh]/.test(dayjsFormat.value))

const maskOptions = reactive({
    mask: dayjsFormat.value
        .replace(/MMM/g, '@@@')
        .replace(/[^-/.:@ ]/g, '#'),
    eager: true,
})

const label = computed(() => marked(props.label))
const modelState = ref(props.modelValue)
const modelValue = computed(() => props.modelValue)
const actionButton = ref<InstanceType<typeof ActionButton>>()

const focused = ref(false)

const disabledDateError = ref('')
const computedError = computed(() => props.error || disabledDateError.value)

const datePicker = ref<InstanceType<typeof Datepicker>>()

const isBetweenMinMax = (d: Date | Date[] | null) => {
    const currentDate = isDateTimePicker.value
        ? dayjs(d as Date).utc(true).startOf('minute')
        : dayjs(d as Date).startOf('day')

    if (props.min) {
        const min = dayjs(props.min).startOf('minute')
        if (currentDate.isBefore(min)) {
            return false
        }
    }

    if (props.max) {
        const max = dayjs(props.max).startOf('minute')
        if (currentDate.isAfter(max)) {
            return false
        }
    }

    if (props.minTime) {
        const { hours, minutes } = props.minTime
        const currentHours = currentDate.hour()
        const currentMinutes = currentDate.minute()

        if (hours && (currentHours < num(hours) || (currentHours === hours && currentMinutes < num(minutes)))) {
            return false
        }
    }

    if (props.maxTime) {
        const { hours, minutes } = props.maxTime
        const currentHours = currentDate.hour()
        const currentMinutes = currentDate.minute()

        if (hours && (currentHours > num(hours) || (currentHours === hours && currentMinutes > num(minutes)))) {
            return false
        }
    }

    return true
}

const disabledWeekDays = computed(() => props.disableWeekends
    ? [0, 6]
    : [],
)

const disabledDates = computed(() => {
    return props.disabledDates
})

const handlePickerValue = (value: string) => {
    modelState.value = value
}

onMounted(() => {
    if (props.initialValue) {
        modelState.value = props.initialValue
    }
})

watch(modelState, (val) => {
    disabledDateError.value = ''
    emit('update:modelValue', val)
})

watch(modelValue, (val) => {
    if (val !== modelState.value) {
        modelState.value = val
    }
})

const num = (value?: string | number): number => typeof value === 'string' ? parseInt(value) : value ?? 0

const handleBlur = () => {
    focused.value = false
    const currentDate = dayjs(modelState.value, dayjsFormat.value)

    if (!currentDate.isValid()) {
        return
    }

    if (props.disableWeekends && [0, 6].includes(currentDate.day())) {
        modelState.value = currentDate.day(1).format(dayjsFormat.value)
    }

    if (props.min) {
        const min = dayjs(props.min)

        if (currentDate.isBefore(min)) {
            modelState.value = min.format(dayjsFormat.value)
        }
    }

    if (props.max) {
        const max = dayjs(props.max)

        if (currentDate.isAfter(max)) {
            modelState.value = max.format(dayjsFormat.value)
        }
    }

    if (props.disabledDates) {
        for (const disabledDate of props.disabledDates) {
            if (dayjs(disabledDate).isSame(currentDate, 'day')) {
                disabledDateError.value = t('input-date-time.disabled-date')
            }
        }
    }

    if (props.minTime) {
        const { hours, minutes } = props.minTime
        const currentHours = currentDate.hour()
        const currentMinutes = currentDate.minute()

        if (hours && (currentHours < num(hours) || (currentHours === hours && currentMinutes < num(minutes)))) {
            modelState.value = currentDate.clone().hour(num(hours)).minute(num(minutes)).format(dayjsFormat.value)
        }
    }

    if (props.maxTime) {
        const { hours, minutes } = props.maxTime
        const currentHours = currentDate.hour()
        const currentMinutes = currentDate.minute()

        if (hours && (currentHours > num(hours) || (currentHours === hours && currentMinutes > num(minutes)))) {
            modelState.value = currentDate.clone().hour(num(hours)).minute(num(minutes)).format(dayjsFormat.value)
        }
    }
}

const { spaceToTop, spaceToBottom, targetRect, containerRect } = useSpaceAvailability(container)

const pickerDimensions = computed(() => {
    // empirical picker popover sizes read in runtime
    return { width: 283, height: isDateTimePicker.value ? 457 : 422 }
})

const teleportDirection = computed(() => {
    if (Math.min(spaceToTop.value, spaceToBottom.value) < 0) {
        return 'center'
    }
    if (pickerDimensions.value.height < spaceToBottom.value) {
        return 'bottom'
    }
    if (pickerDimensions.value.height < spaceToTop.value) {
        return 'top'
    }
    return 'center'
})

const teleportPosition = computed(() => {
    if (teleportDirection.value === 'center' || !targetRect.value || !containerRect.value) {
        return undefined
    }

    return () => {
        const teleportOffset = 10

        const { left: targetLeft, width: targetWidth, bottom: targetBottom } = targetRect.value!
        const { top: containerTop, left: containerLeft, right: containerRight } = containerRect.value!
        const { width: pickerWidth, height: pickerHeight } = pickerDimensions.value

        const idealLeft = targetLeft + ((targetWidth - pickerWidth) / 2)
        const minLeft = containerLeft + teleportOffset
        const maxLeft = containerRight - teleportOffset - pickerWidth
        const left = minLeft < maxLeft ? clamp(minLeft, maxLeft, idealLeft) : idealLeft

        if (pickerHeight < spaceToBottom.value) { // expand to bottom
            return { top: targetBottom + teleportOffset, left }
        } else { // expand to top
            return { top: containerTop + spaceToTop.value - pickerHeight - teleportOffset, left }
        }
    }
})

const updateArrowDirection = (outerMenu?: HTMLElement) => {
    if (teleportDirection.value !== 'center' && outerMenu) {
        const arrow = outerMenu.querySelector(teleportDirection.value === 'top' ? '.dp__arrow_top' : '.dp__arrow_bottom')
        if (arrow) {
            arrow.classList.remove(teleportDirection.value === 'top' ? 'dp__arrow_top' : 'dp__arrow_bottom')
            arrow.classList.add(teleportDirection.value === 'top' ? 'dp__arrow_bottom' : 'dp__arrow_top')
        }
    }
}

const getElementWrapper = () => document.getElementsByClassName('dp__outer_menu_wrap')[0] as HTMLElement | undefined

watch(teleportDirection, () => {
    updateArrowDirection(getElementWrapper())
})

const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        event.stopPropagation()
        hideDatePicker()
    }
}

const revealDatePicker = () => {
    document.addEventListener('keydown', handleEscKey, { capture: true })

    nextTick(() => {
        const outerMenu = getElementWrapper()
        if (outerMenu) {
            outerMenu.style.display = 'block'
            updateArrowDirection(outerMenu)
        }
        if (isNotEmpty(props.customCssVariables)) {
            useCustomCss().applyCssVariables(props.customCssVariables!, outerMenu?.children[0] as HTMLElement)
        }
    })
}
const hideDatePicker = () => {
    document.removeEventListener('keydown', handleEscKey, { capture: true })

    const outerMenu = document.getElementsByClassName('dp__outer_menu_wrap')[0] as HTMLElement | undefined
    if (outerMenu) {
        datePicker.value?.closeMenu()
    }
}

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleEscKey, { capture: true })
})

const handleDatePickerKeyPress = (event: KeyboardEvent) => {
    const code = event.code

    // ENTER
    if (code === 'Enter' && !(event.target as Element).contains(actionButton.value?.$el)) {
        if (!isDateTimePicker.value) {
            event.preventDefault()
            actionButton.value?.$el.click()
        }

        if (isDateTimePicker.value) {
            event.preventDefault()
            datePicker.value?.switchView('time')
        }
    }
}

const toggleDatePicker = (withFocus = false) => {
    const outerMenu = document.getElementsByClassName('dp__outer_menu_wrap')[0] as HTMLElement | undefined
    if (outerMenu?.style.display === 'block') {
        datePicker.value?.closeMenu()
    } else {
        datePicker.value?.openMenu()
        nextTick(() => {
            if (withFocus) {
                const firstFocusableElement = document.querySelector<HTMLElement>('.dp__menu')
                firstFocusableElement?.focus()
                // element will be destroyed so we don't need to add removeEventListener
                firstFocusableElement?.addEventListener('keypress', handleDatePickerKeyPress)
            }
        })
    }
}

const getDataTestId = (value: string) => {
    return `${isDateTimePicker.value ? `input-date-time` : `input-date`}-${value}`
}

const cpIsLoading = inject('isLoading', ref(false))

const addMaska = (input: string) => {
    new MaskInput(input, toRaw(maskOptions))
}

const handleCalendarTriggerKeyDown = (event: KeyboardEvent) => {
    const code = event.code

    if (code === 'Enter' || code === 'Space') {
        toggleDatePicker(true)
        event.preventDefault()
    }
}
</script>

<style lang="sass" scoped>
@import './InputDateTime.sass'
</style>
